import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactSplide from 'Douleutaras/components/Slider/ReactSplide';
import Modal from 'Douleutaras/components/Modal/Modal';
import SliderGallery from 'Douleutaras/components/Slider/SliderGallery';
import { SliderMediaType } from 'Douleutaras/components/Slider/types';
import SliderPreviewItem from 'Douleutaras/components/Slider/parts/SliderPreviewItem';
import { TABLET_BIG } from 'Douleutaras/constants/media-queries';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { getRelationEntitiesFromSingleEntity } from 'Douleutaras/redux/data/jobAttributes/jobAttributesSelectors';
import { sellerProfilesPortfolioSelectors } from 'Douleutaras/redux/data/seller/sellerProfilesPortfolioSlice';
import { sellerProfilesSelectors } from 'Douleutaras/redux/data/seller/sellerProfilesSlice';
import { SplideRef } from 'Douleutaras/types';
import useGtm from 'Douleutaras/hooks/gtm';
import { PageEventNames } from 'Douleutaras/views/pro-profile/components/settings/gtm';

import './ProProfilePortfolioSection.scss';

interface ProProfilePortfolioSectionProps {
  portfolioIsOpenFromOutside: number;
  username: string;
  portfolioDismissCallBack(index: number): void;
}

interface PortfolioMedia {
  url: string;
  thumbUrl: string;
  caption: string;
  altImageText: string;
  type: SliderMediaType;
  autoPlay?: boolean;
  order: number;
}

const ProProfilePortfolioSection = ({ portfolioIsOpenFromOutside, portfolioDismissCallBack, username }
                                    : ProProfilePortfolioSectionProps) => {
  const profile = useAppSelector(state => sellerProfilesSelectors.selectByUsername(state, username));
  const { track } = useGtm();

  const selectPortfolioItems = getRelationEntitiesFromSingleEntity(
    state => sellerProfilesSelectors.selectByUsername(state, username),
    state => sellerProfilesPortfolioSelectors.selectAll(state),
    'portfolioItems');
  const portfolioItemsUnsorted = useAppSelector(state => selectPortfolioItems(state));

  // Will sort the portfolio items based on their order in the database.
  const proPortfolioCollection = useMemo(() => {
    const portfolioItems = portfolioItemsUnsorted.toSorted((a, b) => (a.attributes?.order - b.attributes?.order));

    // Find video with "autoPlay: true" and bring it to front of the array of portfolios
    const profileVideoIndex = portfolioItems
      .findIndex(portfolioItem => Boolean(portfolioItem.attributes.autoPlay));
    if (profileVideoIndex > -1) {
      portfolioItems.unshift(portfolioItems[profileVideoIndex]);
      portfolioItems.splice(profileVideoIndex + 1, 1);
    }

    return portfolioItems
      .map(portfolioItem => {
        const isVideo = Boolean(portfolioItem.attributes.videoUrl);

        return (
          {
            url: isVideo ? portfolioItem.attributes.videoUrl : portfolioItem.attributes.image,
            thumbUrl: isVideo ? portfolioItem.attributes.videoThumbnail : portfolioItem.attributes.imageThumbnail,
            caption: portfolioItem.attributes.description,
            altImageText: portfolioItem.attributes.altImageText,
            type: isVideo ? SliderMediaType.VIDEO : SliderMediaType.IMAGE,
            autoPlay: isVideo ? portfolioItem.attributes.autoPlay : undefined,
            order: portfolioItem.attributes.order
          }
        );
      });

  }, [portfolioItemsUnsorted]);

  const mugshotVideo = profile?.attributes.mugshotVideo;
  const showOnlyMugshot: boolean = !mugshotVideo && portfolioIsOpenFromOutside > -1;

  const profilePic: PortfolioMedia = {
    url: profile?.attributes.mugshot,
    thumbUrl: profile?.attributes.mugshot,
    caption: profile?.attributes.sellerDisplayName,
    altImageText: profile?.attributes.sellerDisplayName,
    type: SliderMediaType.IMAGE,
    order: 0
  };

  // Add profile image as first item and the rest of portfolios
  let portfolioUrls = [];

  if (proPortfolioCollection.length > 0) {
    portfolioUrls = [ ...portfolioUrls, ...proPortfolioCollection ];
  }

  if (showOnlyMugshot) {
    portfolioUrls.unshift(profilePic);
  }

  const portfolioSlides = portfolioUrls.map(slide => {
    return <div styleName="slider__container" key={`key_${slide.url}_${slide.altImageText}`}>
      <SliderPreviewItem
        type={slide.type}
        src={slide.thumbUrl}
        alt={slide.altImageText}
        size="large"
      />
    </div>;
  });

  const portfolioSliderRef = useRef<SplideRef>();
  const [ galleryIsOpenAtIndex, setGalleryIsOpenAtIndex ] = useState(-1);

  useEffect(() => {
    portfolioSliderRef.current.splide.on('click', slide => {
      setGalleryIsOpenAtIndex(slide.index);
    });
  }, []);

  let currentGalleryIndex;
  let galleryIsOpen;

  switch (true) {
    case galleryIsOpenAtIndex > -1:
      currentGalleryIndex = galleryIsOpenAtIndex;
      // @GA_EVENT
      track(PageEventNames.GALLERY_CLICKED);
      galleryIsOpen = true;
      break;
    case portfolioIsOpenFromOutside > -1:
      currentGalleryIndex = portfolioIsOpenFromOutside;
      galleryIsOpen = true;
      break;
    default:
      currentGalleryIndex = -1;
      galleryIsOpen = false;
      break;
  }

  return (
    <div>
      <div styleName="slider">
        <ReactSplide
          ref={portfolioSliderRef}
          options={{
            fixedWidth : '165px',
            fixedHeight: '165px',
            gap: '10px',
            arrows: true,
            focus: 0,
            omitEnd: true,
            breakpoints: {
              [TABLET_BIG.MIN_WIDTH]: {
                arrows: false
              }
            }
          }}
        >
          {portfolioSlides}
        </ReactSplide>
      </div>
      <Modal isOpen={galleryIsOpen} dismissCallback={() => {
        setGalleryIsOpenAtIndex(-1);
        portfolioDismissCallBack(-1);
      }}>
        <SliderGallery openingIndex={currentGalleryIndex}
                       slides={ showOnlyMugshot ? [ portfolioUrls[0] ] : portfolioUrls}
                       displayThumbs={!showOnlyMugshot}
                       liteHeader={showOnlyMugshot}
                       dismissCallback={() => {
                         setGalleryIsOpenAtIndex(-1);
                         portfolioDismissCallBack(-1);
                       }} />
      </Modal>
    </div>
  );
};

export default ProProfilePortfolioSection;
